import * as XLSX from 'xlsx';
import { SysCodeDto } from 'src/dtos/code/sysCodeDto';
import { ItemDto } from 'src/dtos/item/itemDto';
import { ExcelGridConfig } from 'src/utils/excel/excelGridConfig';
import { MST1020_R01_BomDto, MST1020_R01HeaderDto } from './Dto_MST1020_R01';
import {
	createMST1020_R01MaterialIntypeGuide,
	MST1020_R01_DEFAULT_MATERIAL_INTYPE,
	MST1020_R01_EXCEL_UPLOAD_COLUMN_COUNT,
} from './ExcelDefinition_MST1020_R01';

const getFormattedDate = (year: number, month: number, day: number): string => {
	const date = new Date(year, month - 1, day);
	if (
		date.getFullYear() !== year
		|| date.getMonth() !== month - 1
		|| date.getDate() !== day
	) {
		throw new Error('엑셀 날짜 형식이 올바르지 않습니다. YYYY-MM-DD 형식으로 입력해주세요.');
	}

	return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const getMST1020_R01ExcelDate = (value: unknown, defaultValue?: string): string => {
	if (value === null || value === undefined || value === '') {
		return defaultValue || '';
	}

	if (typeof value === 'number') {
		const parsedDate = XLSX.SSF.parse_date_code(value);
		if (!parsedDate) {
			throw new Error('엑셀 날짜 형식이 올바르지 않습니다. YYYY-MM-DD 형식으로 입력해주세요.');
		}
		return getFormattedDate(parsedDate.y, parsedDate.m, parsedDate.d);
	}

	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		return getFormattedDate(value.getFullYear(), value.getMonth() + 1, value.getDate());
	}

	if (typeof value === 'string') {
		const matchedDate = value.trim().match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
		if (matchedDate) {
			return getFormattedDate(Number(matchedDate[1]), Number(matchedDate[2]), Number(matchedDate[3]));
		}
	}

	throw new Error('엑셀 날짜 형식이 올바르지 않습니다. YYYY-MM-DD 형식으로 입력해주세요.');
};

export const MST1020_R01_EXCEL_HEADER_NAMES: Record<string, string> = {
	rowIndex: '',
	prntItemcd: '모품목코드',
	prntItemnm: '모품목명',
	childItemcd: '자품목코드',
	childItemnm: '자품목명',
	itemSpec: '규격',
	locationNm: '보관위치',
	bomSeq: '순서',
	bomUseyn: '사용여부',
	prntItemQty: '모품목수량',
	prntItemUnit: '모품목단위',
	childItemUsage: '소요량',
	childItemUnit: '자품목단위',
	fixLossqty: '고정손실량',
	materialIntype: '자재공급방법',
	lossPercent: '손실율(%)',
	applyBeginDt: '적용시작일',
	applyEndDt: '적용종료일',
	remark: '비고',
};

export const MST1020_R01_EXCEL_COLUMN_ORDER = [
	'prntItemcd', 'prntItemnm', 'childItemcd', 'childItemnm',
	'bomSeq', 'bomUseyn', 'applyBeginDt', 'applyEndDt',
	'prntItemQty', 'prntItemUnit', 'childItemUsage', 'childItemUnit', 'materialIntype',
	'fixLossqty', 'lossPercent', 'remark',
];

/**
 * MST1020_R01의 템플릿 생성과 업로드 변환 규칙을 한 객체로 구성한다.
 * 화면에서 조회한 품목·공통코드를 전달받아 업로드 행 자동 보완과 안내표 생성에 사용한다.
 */
export const createMST1020ExcelConfig = (
	selectedItem: MST1020_R01HeaderDto | null,
	defaultRow: MST1020_R01_BomDto,
	itemCdMap: Map<string, ItemDto>,
	materialIntypeList: SysCodeDto[]
): ExcelGridConfig<MST1020_R01_BomDto> => ({
	headerNames: MST1020_R01_EXCEL_HEADER_NAMES,
	columnOrder: MST1020_R01_EXCEL_COLUMN_ORDER,
	defaultRow,
	template: {
		sheetName: 'BOM 템플릿',
		fileNamePrefix: 'BOM_업로드_템플릿',
		colWidths: [12, 18, 12, 18, 8, 8, 12, 12, 10, 14, 10, 14, 15, 10, 10, 20],
		sampleData: [[
			selectedItem?.itemCd || '',
			selectedItem?.itemNm || '',
			'',
			'',
			1,
			'Y',
			defaultRow.applyBeginDt || '',
			defaultRow.applyEndDt || '',
			1,
			defaultRow.prntItemUnit || 'EA',
			1,
			defaultRow.childItemUnit || 'EA',
			defaultRow.materialIntype || MST1020_R01_DEFAULT_MATERIAL_INTYPE,
			0,
			0,
			'',
		]],
		// 사용자가 코드값을 확인할 수 있도록 같은 시트의 별도 영역에 공통코드 안내표를 생성한다.
		sideGuide: createMST1020_R01MaterialIntypeGuide(materialIntypeList),
	},
	// 안내표가 BOM 데이터로 파싱되지 않도록 기존 양식의 최대 데이터 영역(A:R)까지만 읽는다.
	uploadColumnCount: MST1020_R01_EXCEL_UPLOAD_COLUMN_COUNT,
	requiredHeaders: ['자품목코드'],
	validateBeforeUpload: () => (!selectedItem ? '모품목을 먼저 선택해주세요.' : null),
	valueTransformers: {
		lossPercent: (value) => (
			typeof value === 'string' ? parseFloat(value.replace('%', '')) || 0 : value
		),
		applyBeginDt: (value) => getMST1020_R01ExcelDate(value, defaultRow.applyBeginDt),
		applyEndDt: (value) => getMST1020_R01ExcelDate(value, defaultRow.applyEndDt),
	},
	processRow: (row) => {
		const childItemcd = String(row.childItemcd ?? '').trim();
		const childItem = itemCdMap.get(childItemcd);

		// 기준정보에 없는 코드도 검증 단계에서 식별할 수 있도록 입력 코드는 그대로 유지한다.
		if (!childItem) {
			return { ...row, childItemcd };
		}

		// Excel에는 자품목코드만 입력해도 품목 기준정보의 명칭·규격·보관위치·단위를 자동 반영한다.
		return {
			...row,
			childItemcd: childItem.itemCd,
			childItemnm: childItem.itemNm || '',
			itemSpec: childItem.itemSpec || '',
			locationCd: childItem.locationCd || '',
			locationNm: childItem.locationNm || '',
			childItemUnit: childItem.itemUnit || '',
		};
	},
	createRowMeta: (_row, _index, valueIndex) => ({
		valueIndex,
		inserted: true,
		isModified: true,
		prntItemcd: selectedItem!.itemCd,
		prntItemnm: selectedItem!.itemNm,
	}),
});
